import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Link,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaSpotify } from "react-icons/fa";
import { IoMdArrowForward } from "react-icons/io";

interface PodcastEpisode {
  title: string;
  url: string;
  description?: string;
}

interface SpotifyPodcastProps {
  title?: string;
  subtitle?: string;
  episodes?: PodcastEpisode[];
  spotifyShowUrl?: string;
  bg?: string;
  py?: number;
  columns?: { base: number; md: number; lg: number };
  spacing?: number;
  showViewAllButton?: boolean;
  viewAllButtonText?: string;
  episodeImageSrc?: string;
  episodeDescription?: string;
}

function SpotifyPodcast({
  title = "Listen to our podcast",
  subtitle = "Vision, mission, challenges, immediate goals and more",
  episodes = [],
  spotifyShowUrl = "https://open.spotify.com/show/5b37r393SWE5inP1lOm7G7",
  bg = "white",
  py = 20,
  columns = { base: 1, md: 2, lg: 3 },
  spacing = 8,
  showViewAllButton = true,
  viewAllButtonText = "View all episodes on Spotify",
  episodeImageSrc = "assets/images/logo_1.png",
  episodeDescription = "Discover insights about healthcare AI and the future of medical practice in this enlightening episode.",
}: SpotifyPodcastProps) {
  const sectionBg = useColorModeValue("gray.50", "gray.900");

  return (
    <Box as="section" py={py} bg={bg} width="100%">
      <Box textAlign="center" mb={10}>
        <Flex
          justify="center"
          align="center"
          bg="teal.50"
          w="fit-content"
          mx="auto"
          p={2}
          borderRadius="full"
          mb={4}
        >
          <FaSpotify color="#1DB954" size={24} />
        </Flex>
        <Heading size="xl" mb={4} color="gray.700">
          {title}
        </Heading>
        <Text fontSize="lg" color="gray.500" maxW="container.md" mx="auto">
          {subtitle}
        </Text>
      </Box>

      <Box
        width="100%"
        px={{ base: 4, md: 10, lg: 16 }}
        py={6}
        bg={sectionBg}
      >
        <SimpleGrid columns={columns} spacing={spacing} width="100%">
          {episodes.map((episode, idx) => (
            <Box
              key={idx}
              bg="white"
              p={6}
              borderRadius="xl"
              boxShadow="md"
              height="100%"
              transition="all 0.3s"
              _hover={{ transform: "translateY(-5px)", boxShadow: "lg" }}
            >
              <Flex direction="column" height="100%">
                <Flex mb={4} align="center">
                  <Box
                    borderRadius="lg"
                    overflow="hidden"
                    mr={4}
                    boxShadow="sm"
                    flexShrink={0}
                    width="80px"
                    height="80px"
                  >
                    <Image
                      src={episodeImageSrc}
                      alt={episode.title}
                      width="100%"
                      height="100%"
                      objectFit="cover"
                    />
                  </Box>
                  <VStack align="start" spacing={1}>
                    <Heading size="sm" noOfLines={2}>
                      {episode.title}
                    </Heading>
                    <Text color="gray.500" fontSize="sm">
                      Episode {idx + 1}
                    </Text>
                  </VStack>
                </Flex>
                <Text
                  noOfLines={3}
                  fontSize="sm"
                  color="gray.600"
                  mb={4}
                  flex="1"
                >
                  {episode.description || episodeDescription}
                </Text>
                <Button
                  as={Link}
                  href={episode.url}
                  isExternal
                  size="sm"
                  leftIcon={<FaSpotify />}
                  colorScheme="green"
                  variant="outline"
                  borderRadius="full"
                  width="100%"
                  justifyContent="center"
                  _hover={{ bg: "green.50" }}
                >
                  Listen on Spotify
                </Button>
              </Flex>
            </Box>
          ))}
        </SimpleGrid>

        {showViewAllButton && (
          <Box textAlign="center" mt={10}>
            <Button
              as={Link}
              href={spotifyShowUrl}
              isExternal
              rightIcon={<IoMdArrowForward />}
              colorScheme="green"
              variant="outline"
              borderRadius="full"
              px={8}
              _hover={{ transform: "translateY(-2px)" }}
              transition="all 0.2s"
            >
              {viewAllButtonText}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default SpotifyPodcast; 